import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'sonner';

const BASE_URL = import.meta.env.VITE_APP_API_URL;
const getToken = () => localStorage.getItem('authx_customer_token');

const customerApi = axios.create({ baseURL: BASE_URL });
customerApi.interceptors.request.use(cfg => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const adminApi = axios.create({ baseURL: BASE_URL });
adminApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('authx_access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const t = (name, fn) => createAsyncThunk(name, async (arg, { rejectWithValue }) => {
  try { return await fn(arg); }
  catch (err) {
    toast.error(err.response?.data?.message || err.message);
    return rejectWithValue(err.response?.data?.message || err.message);
  }
});

// Admin
export const fetchScratchCampaigns = t('scratch/fetchCampaigns', async () => {
  const { data } = await adminApi.get('ScratchCard/campaigns');
  return data.data;
});

export const createScratchCampaign = t('scratch/createCampaign', async (dto) => {
  const { data } = await adminApi.post('ScratchCard/campaigns', dto);
  toast.success('Campaign created!');
  return data.data;
});

export const setScratchCampaignActive = t('scratch/setActive', async ({ id, active }) => {
  await adminApi.patch(`ScratchCard/campaigns/${id}/status?active=${active}`);
  return { id, active };
});

export const deleteScratchCampaign = t('scratch/delete', async (id) => {
  await adminApi.delete(`ScratchCard/campaigns/${id}`);
  toast.success('Campaign deleted');
  return id;
});

// Customer
export const fetchMyCards = t('scratch/fetchMyCards', async (companyId) => {
  const { data } = await customerApi.get(`ScratchCard/my-cards?companyId=${companyId}`);
  return data.data;
});

export const scratchCard = t('scratch/scratchCard', async (cardId) => {
  const { data } = await customerApi.post(`ScratchCard/scratch/${cardId}`);
  return data.data;
});

const scratchSlice = createSlice({
  name: 'scratch',
  initialState: {
    campaigns: [],
    myCards: [],
    scratchResult: null,
    loading: false,
    actionLoading: false,
  },
  reducers: {
    clearScratchResult: (s) => { s.scratchResult = null; }
  },
  extraReducers: (b) => {
    b
      .addCase(fetchScratchCampaigns.pending, (s) => { s.loading = true; })
      .addCase(fetchScratchCampaigns.fulfilled, (s, a) => { s.loading = false; s.campaigns = a.payload || []; })
      .addCase(fetchScratchCampaigns.rejected, (s) => { s.loading = false; })
      .addCase(createScratchCampaign.fulfilled, (s, a) => { s.campaigns = [a.payload, ...s.campaigns]; })
      .addCase(setScratchCampaignActive.fulfilled, (s, a) => {
        s.campaigns = s.campaigns.map(c =>
          c.campaignId === a.payload.id ? { ...c, isActive: a.payload.active } : c);
      })
      .addCase(deleteScratchCampaign.fulfilled, (s, a) => {
        s.campaigns = s.campaigns.filter(c => c.campaignId !== a.payload);
      })
      .addCase(fetchMyCards.pending, (s) => { s.loading = true; })
      .addCase(fetchMyCards.fulfilled, (s, a) => { s.loading = false; s.myCards = a.payload || []; })
      .addCase(fetchMyCards.rejected, (s) => { s.loading = false; })
      .addCase(scratchCard.pending, (s) => { s.actionLoading = true; })
      .addCase(scratchCard.fulfilled, (s, a) => {
        s.actionLoading = false;
        s.scratchResult = a.payload;
        // Update card in list
        s.myCards = s.myCards.map(c =>
          c.cardId === a.meta.arg
            ? { ...c, isScratched: true, prize: a.payload }
            : c
        );
      })
      .addCase(scratchCard.rejected, (s) => { s.actionLoading = false; });
  }
});

export const { clearScratchResult } = scratchSlice.actions;
export default scratchSlice.reducer;